/**
 * Phase II · M-P5 — CLI intent capture
 *
 * Client-side bindings for the OrgX intents API (M-P2). The wizard POSTs a raw
 * intent, the server classifies it to an agent + generates a plan preview, and
 * the CLI surface renders that payload with the OrgX terminal vocabulary
 * (agent-colored routing line, mono confidence decimal, box-drawn plan, `>>`
 * hotkey bar). Approve / archive PATCH the row; "edit in browser" hands off to
 * `/command?intent=<slug>`.
 *
 * The contract mirrors `orgx/types/intents.ts` in the main monorepo; the shape
 * is duplicated here (not imported) because orgx-wizard is a separate npm
 * package shipped independently. Keeping the types narrow + local lets us
 * avoid a cross-repo dependency while still being strict at the boundary.
 */

import {
  buildOrgxApiUrl,
  resolveOrgxAuth,
  type ResolveOrgxAuthOptions,
} from "./auth.js";
import { isRecord } from "./fs.js";

/** Canonical agent slugs the classifier can emit (mirror of @/types/intents). */
export const AGENT_SLUGS = [
  "mark",
  "eli",
  "dana",
  "pace",
  "sage",
  "orion",
  "xandy",
] as const;

export type AgentSlug = (typeof AGENT_SLUGS)[number];

export const INTENT_STATUSES = [
  "pending",
  "approved",
  "adjusted",
  "archived",
] as const;

export type IntentStatus = (typeof INTENT_STATUSES)[number];

/** Confidence bucket returned by the server. */
export type IntentConfidence = "low" | "med" | "high";

export interface IntentPlanStep {
  title: string;
  detail?: string;
  agent_slug?: AgentSlug;
}

export interface IntentPlanPreview {
  steps: IntentPlanStep[];
  eta: string;
  confidence: IntentConfidence;
}

export interface Intent {
  id: string;
  workspace_id: string;
  slug: string;
  text: string;
  status: IntentStatus;
  suggested_agent_slug: AgentSlug | null;
  plan_preview: IntentPlanPreview;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateIntentInput {
  workspace_id: string;
  text: string;
}

export interface UpdateIntentInput {
  status: Exclude<IntentStatus, "pending">;
  text?: string;
  plan_preview?: IntentPlanPreview;
}

export interface OrgxIntentsClientOptions extends ResolveOrgxAuthOptions {}

/** Role labels for the `→ Agent · Role` routing line. */
export const AGENT_ROLES: Record<AgentSlug, string> = {
  mark: "Partnerships",
  eli: "Engineering",
  dana: "Brand",
  pace: "Product",
  sage: "Strategy",
  orion: "Routing",
  xandy: "Default",
};

/**
 * Deterministic bucket → decimal mapping used by the CLI render layer.
 *
 * The design spec (Phase II · M-P0 Surface 3) locks the CLI to a mono
 * confidence decimal (`0.87`) and explicitly rejects `low / med / high`
 * buckets. The M-P2 API still returns the bucket, so we render a
 * representative decimal per bucket here. Values are chosen to match the
 * spec's example band (0.55 / 0.78 / 0.92) and to read like the output of
 * a real classifier rather than round magic numbers.
 */
export const CONFIDENCE_DECIMAL: Record<IntentConfidence, number> = {
  low: 0.55,
  med: 0.78,
  high: 0.92,
};

/**
 * Per-agent ANSI 256-color codes used to tint the `→ Agent · Role` routing
 * line. Values mirror the tokens called out in the M-P0 spec so the terminal
 * identity stays aligned with the web `--agent-accent-rgb` directory.
 */
export const AGENT_ANSI_256: Record<AgentSlug, number> = {
  pace: 34, // green
  eli: 44, // cyan
  mark: 208, // orange
  sage: 135, // purple
  orion: 214, // amber
  dana: 199, // pink
  xandy: 37, // teal
};

function parseAgentSlug(value: unknown): AgentSlug | null {
  if (typeof value !== "string") return null;
  return (AGENT_SLUGS as readonly string[]).includes(value)
    ? (value as AgentSlug)
    : null;
}

function parseStatus(value: unknown): IntentStatus | null {
  if (typeof value !== "string") return null;
  return (INTENT_STATUSES as readonly string[]).includes(value)
    ? (value as IntentStatus)
    : null;
}

function parseConfidence(value: unknown): IntentConfidence {
  if (value === "low" || value === "med" || value === "high") return value;
  return "med";
}

function parsePlanStep(value: unknown): IntentPlanStep | null {
  if (!isRecord(value)) return null;
  const title = typeof value.title === "string" ? value.title.trim() : "";
  if (!title) return null;

  const detail =
    typeof value.detail === "string" && value.detail.trim().length > 0
      ? value.detail
      : undefined;
  const agentSlug = parseAgentSlug(value.agent_slug);

  const step: IntentPlanStep = { title };
  if (detail !== undefined) step.detail = detail;
  if (agentSlug !== null) step.agent_slug = agentSlug;
  return step;
}

function parsePlanPreview(value: unknown): IntentPlanPreview {
  if (!isRecord(value)) {
    return { steps: [], eta: "under a week", confidence: "med" };
  }

  const steps = Array.isArray(value.steps)
    ? value.steps
        .map(parsePlanStep)
        .filter((step): step is IntentPlanStep => step !== null)
    : [];
  const eta =
    typeof value.eta === "string" && value.eta.trim().length > 0
      ? value.eta
      : "under a week";
  return {
    steps,
    eta,
    confidence: parseConfidence(value.confidence),
  };
}

export function parseIntent(value: unknown): Intent | null {
  if (!isRecord(value)) return null;

  const id = typeof value.id === "string" ? value.id : null;
  const workspaceId =
    typeof value.workspace_id === "string" ? value.workspace_id : null;
  const slug = typeof value.slug === "string" ? value.slug : null;
  const text = typeof value.text === "string" ? value.text : null;
  const status = parseStatus(value.status);
  const createdAt =
    typeof value.created_at === "string" ? value.created_at : null;
  const updatedAt =
    typeof value.updated_at === "string" ? value.updated_at : null;

  if (!id || !workspaceId || !slug || !text || !status || !createdAt || !updatedAt) {
    return null;
  }

  return {
    id,
    workspace_id: workspaceId,
    slug,
    text,
    status,
    suggested_agent_slug: parseAgentSlug(value.suggested_agent_slug),
    plan_preview: parsePlanPreview(value.plan_preview),
    approved_at:
      typeof value.approved_at === "string" ? value.approved_at : null,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatHttpError(status: number, body: unknown): string {
  if (typeof body === "string" && body.trim().length > 0) {
    return `HTTP ${status}: ${body}`;
  }
  if (isRecord(body)) {
    // The intents API wraps errors as `{ error: { code, message } }`.
    if (isRecord(body.error)) {
      const message =
        typeof body.error.message === "string" ? body.error.message : null;
      const code = typeof body.error.code === "string" ? body.error.code : null;
      if (message) return `HTTP ${status} (${code ?? "error"}): ${message}`;
    }
    if (typeof body.error === "string" && body.error.trim().length > 0) {
      return `HTTP ${status}: ${body.error}`;
    }
  }
  return `HTTP ${status}`;
}

async function requireOrgxAuth(options: OrgxIntentsClientOptions = {}) {
  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    throw new Error(
      "No OrgX API key configured. Run `orgx-wizard auth login` or `orgx-wizard auth set-key <oxk_...>` first.",
    );
  }
  return auth;
}

/**
 * POST /api/v1/intents — create a pending intent.
 *
 * The server classifies the raw text to an agent slug and generates a plan
 * preview before persisting. The returned `Intent` row carries the slug used
 * for the browser handoff URL (`/command?intent=<slug>`).
 */
export async function createIntent(
  input: CreateIntentInput,
  options: OrgxIntentsClientOptions = {},
): Promise<Intent> {
  const text = input.text.trim();
  if (!text) {
    throw new Error("Intent text is required.");
  }
  if (text.length > 4_000) {
    throw new Error("Intent text exceeds 4,000 characters.");
  }

  const workspaceId = input.workspace_id.trim();
  if (!workspaceId) {
    throw new Error("Workspace id is required.");
  }

  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl("/v1/intents", auth.baseUrl);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ workspace_id: workspaceId, text }),
    signal: AbortSignal.timeout(15_000),
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(
      `Failed to create intent. ${formatHttpError(response.status, body)}`,
    );
  }

  const intent = parseIntent(body);
  if (!intent) {
    throw new Error("OrgX returned an unexpected intent payload.");
  }
  return intent;
}

/**
 * PATCH /api/v1/intents/:id — transition an intent to approved / adjusted /
 * archived. The hotkey bar uses this for `[A]pprove` and `[X]archive`.
 */
export async function updateIntent(
  id: string,
  input: UpdateIntentInput,
  options: OrgxIntentsClientOptions = {},
): Promise<Intent> {
  const trimmedId = id.trim();
  if (!trimmedId) {
    throw new Error("Intent id is required.");
  }

  const auth = await requireOrgxAuth(options);
  const url = buildOrgxApiUrl(`/v1/intents/${encodeURIComponent(trimmedId)}`, auth.baseUrl);
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
    signal: AbortSignal.timeout(15_000),
  });
  const body = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(
      `Failed to update intent. ${formatHttpError(response.status, body)}`,
    );
  }

  const intent = parseIntent(body);
  if (!intent) {
    throw new Error("OrgX returned an unexpected intent update payload.");
  }
  return intent;
}

/**
 * Build the handoff URL for the web composer. Used by the `[E]dit in browser`
 * hotkey and by the approve flow's confirmation line.
 */
export function buildIntentHandoffUrl(
  slug: string,
  options: { baseUrl?: string } = {},
): string {
  const base = options.baseUrl?.trim() || "https://useorgx.com";
  const normalized = base.replace(/\/+$/, "");
  return `${normalized}/command?intent=${encodeURIComponent(slug)}`;
}
